import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from "recharts";
import { Loader2, Play, Target, Activity } from "lucide-react";
import { formatUSD, formatPct } from "../lib/format";
import { toast } from "sonner";

const PRESETS = [
  { symbol: "BTC-USD", label: "Bitcoin" },
  { symbol: "ETH-USD", label: "Ethereum" },
  { symbol: "SPY", label: "S&P 500 ETF" },
  { symbol: "QQQ", label: "Nasdaq 100" },
  { symbol: "AAPL", label: "Apple" },
  { symbol: "TSLA", label: "Tesla" },
  { symbol: "NVDA", label: "NVIDIA" },
  { symbol: "COIN", label: "Coinbase" },
  { symbol: "MSTR", label: "Strategy" },
  { symbol: "GC=F", label: "Or (Gold)" },
  { symbol: "TLT", label: "US Treasury 20+" },
];

const STRATEGY_INFO = {
  buy_hold: "Achat unique puis conservation sur toute la période. Vise à capter la tendance longue.",
  dca: "Investissement d'un montant fixe chaque mois. Lisse le prix d'achat et enlève l'émotion du timing.",
  sma_cross: "Achat quand la moyenne mobile courte croise au-dessus de la longue, vente quand elle passe en-dessous.",
};

function Stat({ label, value, sub, positive }) {
  return (
    <div className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`text-xl font-mono-num font-semibold mt-1 ${positive === true ? "text-emerald-400" : positive === false ? "text-rose-400" : ""}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function SimpleBacktestPanel() {
  const [form, setForm] = useState({
    symbol: "BTC-USD",
    strategy: "buy_hold",
    start_date: "2021-01-01",
    end_date: new Date().toISOString().slice(0, 10),
    initial_capital: 10000,
    dca_amount: 200,
    sma_short: 20,
    sma_long: 50,
  });
  const [result, setResult] = useState(null);

  const mutation = useMutation({
    mutationFn: async (payload) => (await api.post("/backtest", payload)).data,
    onSuccess: (d) => {
      setResult(d);
      toast.success("Backtest exécuté");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card className="p-6 bg-card border-white/5 lg:col-span-1 h-fit" data-testid="backtest-form">
        <h3 className="font-display font-bold text-lg mb-4">Paramètres</h3>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Actif</Label>
            <Select value={form.symbol} onValueChange={(v) => setField("symbol", v)}>
              <SelectTrigger data-testid="backtest-symbol-select" className="mt-1.5 bg-white/[0.03] border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRESETS.map((p) => (
                  <SelectItem key={p.symbol} value={p.symbol}>
                    {p.label} <span className="text-zinc-500 ml-2">{p.symbol}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className="mt-2 bg-white/[0.03] border-white/10 font-mono-num text-sm"
              placeholder="Ou tapez un ticker (ex: AMZN)"
              value={form.symbol}
              onChange={(e) => setField("symbol", e.target.value.toUpperCase())}
              data-testid="backtest-symbol-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Stratégie</Label>
            <Select value={form.strategy} onValueChange={(v) => setField("strategy", v)}>
              <SelectTrigger data-testid="backtest-strategy-select" className="mt-1.5 bg-white/[0.03] border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="buy_hold">Buy &amp; Hold</SelectItem>
                <SelectItem value="dca">DCA (Dollar-Cost Averaging)</SelectItem>
                <SelectItem value="sma_cross">Croisement SMA</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-2 text-xs text-zinc-500 leading-relaxed" data-testid="strategy-explainer">
              {STRATEGY_INFO[form.strategy]}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Début</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setField("start_date", e.target.value)} className="mt-1.5 bg-white/[0.03] border-white/10" data-testid="backtest-start-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Fin</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setField("end_date", e.target.value)} className="mt-1.5 bg-white/[0.03] border-white/10" data-testid="backtest-end-date" />
            </div>
          </div>

          {form.strategy !== "dca" && (
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Capital initial ($)</Label>
              <Input type="number" value={form.initial_capital} onChange={(e) => setField("initial_capital", parseFloat(e.target.value) || 0)} className="mt-1.5 bg-white/[0.03] border-white/10 font-mono-num" data-testid="backtest-capital" />
            </div>
          )}

          {form.strategy === "dca" && (
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Montant mensuel ($)</Label>
              <Input type="number" value={form.dca_amount} onChange={(e) => setField("dca_amount", parseFloat(e.target.value) || 0)} className="mt-1.5 bg-white/[0.03] border-white/10 font-mono-num" data-testid="backtest-dca" />
            </div>
          )}

          {form.strategy === "sma_cross" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">SMA courte</Label>
                <Input type="number" value={form.sma_short} onChange={(e) => setField("sma_short", parseInt(e.target.value) || 20)} className="mt-1.5 bg-white/[0.03] border-white/10 font-mono-num" data-testid="backtest-sma-short" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">SMA longue</Label>
                <Input type="number" value={form.sma_long} onChange={(e) => setField("sma_long", parseInt(e.target.value) || 50)} className="mt-1.5 bg-white/[0.03] border-white/10 font-mono-num" data-testid="backtest-sma-long" />
              </div>
            </div>
          )}

          <Button onClick={() => mutation.mutate(form)} disabled={mutation.isPending} className="w-full bg-emerald-500 text-black hover:bg-emerald-300 mt-4" data-testid="backtest-run-btn">
            {mutation.isPending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calcul en cours…</>
            ) : (
              <><Play className="w-4 h-4 mr-2" /> Lancer le backtest</>
            )}
          </Button>
        </div>
      </Card>

      <div className="lg:col-span-2 space-y-6">
        {result ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="backtest-metrics">
              <Stat label="Rendement total" value={formatPct(result.total_return_pct)} positive={result.total_return_pct >= 0} />
              <Stat label="CAGR" value={formatPct(result.cagr_pct)} positive={result.cagr_pct >= 0} />
              <Stat label="Max Drawdown" value={formatPct(result.max_drawdown_pct)} positive={false} />
              <Stat label="Sharpe" value={result.sharpe_ratio?.toFixed(2)} />
              <Stat label="Capital initial" value={formatUSD(result.initial_capital)} />
              <Stat label="Valeur finale" value={formatUSD(result.final_value)} />
              <Stat label="# Trades" value={result.num_trades} />
              <Stat label="B&H (bench.)" value={formatUSD(result.benchmark_buy_hold_value)} />
            </div>

            <Card className="p-6 bg-card border-white/5" data-testid="backtest-chart">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-4 h-4 text-emerald-400" />
                <h3 className="font-display font-bold text-lg">Courbe d'équité</h3>
              </div>
              <div className="h-[380px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={result.equity_curve}>
                    <CartesianGrid stroke="#27272A" strokeDasharray="3 3" opacity={0.4} />
                    <XAxis dataKey="date" stroke="#71717A" fontSize={11} tickLine={false} />
                    <YAxis stroke="#71717A" fontSize={11} tickLine={false} tickFormatter={(v) => formatUSD(v, 0)} />
                    <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 8 }} labelStyle={{ color: "#A1A1AA" }} formatter={(v) => formatUSD(v)} />
                    <ReferenceLine y={result.initial_capital} stroke="#F59E0B" strokeDasharray="4 4" opacity={0.5} />
                    <Line type="monotone" dataKey="value" stroke="#00BF63" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </>
        ) : (
          <Card className="p-16 bg-card border-white/5 text-center" data-testid="backtest-empty">
            <Target className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <div className="text-zinc-400">Configurez vos paramètres à gauche et lancez votre premier backtest.</div>
            <div className="text-xs text-zinc-600 mt-2">Astuce : essayez BTC-USD en DCA sur 3 ans pour voir l'effet du lissage.</div>
          </Card>
        )}
      </div>
    </div>
  );
}
